import { useEffect, useMemo, useState } from "react";
import Ethnicity from "../../models/serviceModels/Ethnicity";
import Gender from "../../models/serviceModels/Gender";
import Race from "../../models/serviceModels/Race";
import { getEthnicities, getGenders, getRaces } from "../../services/clientsService";
import ApiErrorMessages from "../UI/ApiErrorMessages";
import Loader from "../UI/Loader";
import Modal from "../UI/Modal";
import { getGenderPreviewOptions, getRacePreviewOptions } from "../../utils/demographicsSchema";

interface DemographicsOptionsInfoModalProps {
    visible: boolean;
    onClose: () => void;
}

const getOptionGroup = (title: string, options: string[], description?: string) => {
    return (
        <div className="border rounded padding-3">
            <h4>{title}</h4>
            {description && <p>{description}</p>}
            {options.length === 0 && <p>No options are currently available to preview.</p>}
            {options.length > 0 && <div>
                {options.map(option => {
                    return <div key={`${title}-${option}`} className="margin-bottom-2">- {option}</div>;
                })}
            </div>}
        </div>
    );
};

const DemographicsOptionsInfoModal = ({ visible, onClose }: DemographicsOptionsInfoModalProps) => {
    const [apiError, setApiError] = useState<any>();
    const [ethnicities, setEthnicities] = useState<Ethnicity[]>([]);
    const [genders, setGenders] = useState<Gender[]>([]);
    const [loading, setLoading] = useState(false);
    const [races, setRaces] = useState<Race[]>([]);

    useEffect(() => {
        if (!visible) {
            return;
        }

        setApiError(undefined);
        setLoading(true);

        Promise.all([getGenders(), getRaces(), getEthnicities()])
            .then(([loadedGenders, loadedRaces, loadedEthnicities]) => {
                setGenders(loadedGenders);
                setRaces(loadedRaces);
                setEthnicities(loadedEthnicities);
            })
            .catch(error => {
                console.error(error);
                setApiError(error);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [visible]);

    const defaultGenderOptions = useMemo(() => {
        if (genders.length > 0) {
            return genders.map(gender => gender.name);
        }

        return getGenderPreviewOptions({ demographicsGenderMode: 'current' });
    }, [genders]);

    const simplifiedGenderOptions = useMemo(() => {
        return getGenderPreviewOptions({ demographicsGenderMode: 'simplified' });
    }, []);

    const currentRaceOptions = useMemo(() => races.map(race => race.name), [races]);
    const currentEthnicityOptions = useMemo(() => ethnicities.map(ethnicity => ethnicity.name), [ethnicities]);

    const combinedRaceEthnicityOptions = useMemo(() => {
        return getRacePreviewOptions(races, { demographicsRaceMode: 'combined' });
    }, [races]);

    return (
        <Modal
            id="demographics-options-info-modal"
            visible={visible}
            className="medium"
            title="Demographic Option Differences"
            onClose={onClose}
        >
            <p>Compare the current default questions with the alternate options before you save changes for your organization.</p>
            <ApiErrorMessages error={apiError} />
            {loading && !apiError && <Loader />}
            {!loading && <div>
                <div className="row margin-bottom-4">
                    <div className="column">
                        <h3>Gender Options</h3>
                    </div>
                </div>
                <div className="row margin-bottom-5">
                    <div className="column-medium-6 margin-bottom-3">
                        {getOptionGroup(
                            'Default Options',
                            defaultGenderOptions,
                            'Uses the current Count Bubble gender list.'
                        )}
                    </div>
                    <div className="column-medium-6 margin-bottom-3">
                        {getOptionGroup(
                            'Simplified Options',
                            simplifiedGenderOptions,
                            'Maps responses into Male, Female, and Other.'
                        )}
                    </div>
                </div>

                <div className="row margin-bottom-4">
                    <div className="column">
                        <h3>Race and Ethnicity Options</h3>
                    </div>
                </div>
                <div className="row">
                    <div className="column-medium-6 margin-bottom-3">
                        <div className="border rounded padding-3">
                            <h4>Default Options</h4>
                            <p>Uses separate Race and Ethnicity questions with the current Count Bubble categories.</p>
                            <div className="margin-bottom-3">
                                <h5>Ethnicity</h5>
                                {currentEthnicityOptions.length === 0 && <p>No ethnicity options are currently available to preview.</p>}
                                {currentEthnicityOptions.length > 0 && currentEthnicityOptions.map(option => {
                                    return <div key={`default-ethnicity-${option}`} className="margin-bottom-2">- {option}</div>;
                                })}
                            </div>
                            <div>
                                <h5>Race</h5>
                                {currentRaceOptions.length === 0 && <p>No race options are currently available to preview.</p>}
                                {currentRaceOptions.length > 0 && currentRaceOptions.map(option => {
                                    return <div key={`default-race-${option}`} className="margin-bottom-2">- {option}</div>;
                                })}
                            </div>
                        </div>
                    </div>
                    <div className="column-medium-6 margin-bottom-3">
                        {getOptionGroup(
                            'OMB-Aligned Combined Options',
                            combinedRaceEthnicityOptions,
                            'Uses one Race/Ethnicity question with the combined OMB-aligned categories and positive ethnicity responses only.'
                        )}
                    </div>
                </div>
            </div>}
        </Modal>
    );
};

export default DemographicsOptionsInfoModal;
